import { useState, type ReactElement, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  BackLink,
  Button,
  Container,
  Dialog,
  FileTrigger,
  Heading,
  Icon,
  NavRow,
  RowGroup,
  Screen,
  SegmentedControl,
  Spinner,
  Text,
  TextInput,
  Toggle,
  useTheme,
  useToast,
} from '@fluent/ui';
import { useAuth, useAuthStore, type Profile } from '../auth/authStore.js';
import { supabase } from '../lib/supabase.js';
import i18n from '../lib/i18n.js';
import { isSoundMuted, playSfx, setSoundMuted } from '../lesson/sounds.js';
import { ShieldGlyph, PhoneGlyph } from '../profile/icons.js';
import {
  CameraGlyph,
  CardsGlyph,
  ContrastGlyph,
  GlobeGlyph,
  MailGlyph,
  RefreshGlyph,
  SpeakerGlyph,
  TargetGlyph,
  TrashGlyph,
} from '../settings/icons.js';
import './Settings.css';

const GOALS = [5, 10, 15, 20] as const;
type Goal = (typeof GOALS)[number];

const NEW_CARD_LIMITS = [5, 10, 15, 20] as const;
type NewCardLimit = (typeof NEW_CARD_LIMITS)[number];

const LOCALES = [
  { code: 'ka', label: 'ქართული' },
  { code: 'en', label: 'English' },
] as const;
type Locale = (typeof LOCALES)[number]['code'];

type AvatarBusy = 'upload' | 'sync' | 'remove' | null;

type PrefField = 'daily_goal_minutes' | 'flashcards_new_per_day' | 'locale';

/**
 * Last-tap-wins + last-confirmed bookkeeping for the optimistic preference
 * saves. MODULE scope (keyed `${userId}:${field}`), not a component ref —
 * a per-instance ref resets on unmount/remount, which would let a stale
 * in-flight write from a previous visit pass its token check and clobber
 * newer state.
 */
const prefWriteSeq = new Map<string, number>();
const prefConfirmed = new Map<string, unknown>();

/**
 * Settings — mutable user preferences (grouped-cards rebuild, DECISIONS
 * 2026-07-19). Sub-screen of Profile: BackLink, no bottom-nav. Four
 * RowGroup cards — identity+photo, learning, app, account — in Profile's
 * exact family language; the extruded-3D chunk is reserved for the
 * controls (SegmentedControl keys, Toggle, dialog Buttons).
 *
 * Preference saves are OPTIMISTIC: the key flips instantly, the Supabase
 * write runs behind, and on failure the value reverts (plain fade, no
 * pop) with a danger toast. Avatar operations stay honestly async
 * (trailing spinner in the row) because an upload is a real wait.
 *
 * <SettingsView> is presentational so /settings-review can mount it with
 * fabricated data; the connected wrapper feeds it from live hooks.
 */
export function Settings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const setProfile = useAuthStore((s) => s.setProfile);
  const { toast } = useToast();

  const [avatarBusy, setAvatarBusy] = useState<AvatarBusy>(null);
  const [avatarNotice, setAvatarNotice] = useState<string | null>(null);
  const [soundOn, setSoundOn] = useState(() => !isSoundMuted());

  if (!user || !profile) return null;

  const currentGoal = (profile.daily_goal_minutes ?? 10) as Goal;
  const currentNewCards = (profile.flashcards_new_per_day ?? 10) as NewCardLimit;
  const currentLocale = (profile.locale ?? 'ka') as Locale;
  const hasOauthPicture = Boolean(
    user.user_metadata?.picture || user.user_metadata?.avatar_url,
  );
  const displayName = profile.display_name ?? user.email?.split('@')[0] ?? '';

  /**
   * Optimistic profile-field save: flip now, write behind. On failure the
   * field converges to SERVER truth (refetch; last-confirmed value as the
   * offline fallback) — never to the previous optimistic value — with a
   * danger toast. Success and failure both merge ONLY the written field:
   * spreading the whole returned row would clobber other fields'
   * in-flight optimistic values with stale state.
   */
  const saveField = <F extends PrefField>(
    field: F,
    next: Profile[F],
    applyValue?: (settled: Profile[F]) => void,
  ) => {
    const before = useAuthStore.getState().profile;
    if (!before) return;
    const prev = before[field];
    if (prev === next) return;
    const key = `${user.id}:${field}`;
    // Seed the confirmed baseline from the store only when nothing is in
    // flight (an in-flight write means the store value is optimistic).
    if (!prefConfirmed.has(key) && !prefWriteSeq.has(key)) prefConfirmed.set(key, prev);
    const token = (prefWriteSeq.get(key) ?? 0) + 1;
    prefWriteSeq.set(key, token);
    setProfile({ ...before, [field]: next });
    // Computed single-key literals widen to an index signature, which the
    // generated Update type rejects — narrow it back to the field map.
    const payload = { [field]: next } as { [K in PrefField]?: Profile[K] };
    void (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .update(payload)
        .eq('id', user.id)
        .select()
        .single();
      if (prefWriteSeq.get(key) !== token) return; // superseded by a newer tap
      const current = useAuthStore.getState().profile;
      if (!current) return;
      if (!error && data) {
        prefConfirmed.set(key, data[field]);
        setProfile({ ...current, [field]: data[field] });
        return;
      }
      const { data: fresh } = await supabase
        .from('profiles')
        .select()
        .eq('id', user.id)
        .single();
      if (prefWriteSeq.get(key) !== token) return;
      const cur2 = useAuthStore.getState().profile;
      if (!cur2) return;
      const settled = (fresh ? fresh[field] : (prefConfirmed.get(key) ?? prev)) as Profile[F];
      if (fresh) prefConfirmed.set(key, fresh[field]);
      setProfile({ ...cur2, [field]: settled });
      applyValue?.(settled);
      toast({ tone: 'danger', message: t('settings.error_save') });
    })();
  };

  const setAvatarUrl = async (url: string | null) => {
    const current = useAuthStore.getState().profile;
    const { data, error } = await supabase
      .from('profiles')
      .update({ avatar_url: url })
      .eq('id', user.id)
      .select()
      .single();
    if (error || !data || !current) {
      toast({ tone: 'danger', message: t('settings.error_save') });
      return;
    }
    // Only the written column — full-row spread would clobber in-flight
    // optimistic preference values.
    setProfile({ ...current, avatar_url: data.avatar_url });
  };

  const onPickAvatar = async (file: File) => {
    setAvatarNotice(null);
    if (!file.type.match(/^image\/(jpeg|png|webp)$/)) {
      setAvatarNotice(t('settings.avatar.error_type'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setAvatarNotice(t('settings.avatar.error_size'));
      return;
    }
    setAvatarBusy('upload');
    // Path layout matches the storage RLS: `<user_id>/<random>.<ext>`. A
    // random suffix per upload bypasses CDN caches pinning the old image.
    const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
    const path = `${user.id}/${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true, contentType: file.type });
    if (upErr) {
      setAvatarBusy(null);
      toast({ tone: 'danger', message: t('settings.avatar.error_upload') });
      return;
    }
    const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path);
    await setAvatarUrl(pub.publicUrl);
    setAvatarBusy(null);
  };

  const onSyncOAuth = async () => {
    setAvatarNotice(null);
    setAvatarBusy('sync');
    // Cast: regenerated Supabase types are missing this RPC (we don't
    // auto-regen per migration). Server enforces auth.uid().
    const sb = supabase as unknown as {
      rpc: (fn: string) => Promise<{ data: string | null; error: unknown }>;
    };
    const { data, error } = await sb.rpc('sync_oauth_avatar');
    setAvatarBusy(null);
    if (error) {
      toast({ tone: 'danger', message: t('settings.avatar.error_sync') });
      return;
    }
    if (!data) {
      setAvatarNotice(t('settings.avatar.no_oauth_picture'));
      return;
    }
    const current = useAuthStore.getState().profile;
    if (current) setProfile({ ...current, avatar_url: data });
  };

  const onRemoveAvatar = async () => {
    setAvatarNotice(null);
    setAvatarBusy('remove');
    await setAvatarUrl(null);
    setAvatarBusy(null);
  };

  /**
   * Real account deletion — /api/delete-account runs the GDPR pipeline
   * (Paddle cancel → avatar wipe → webhook log wipe → auth row delete).
   * On success: sign out + bounce to landing. Returns false on failure so
   * the dialog shows the generic error and the user can retry.
   */
  const onConfirmDelete = async (): Promise<boolean> => {
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) return false;
      const r = await fetch('/api/delete-account', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return false;
      await supabase.auth.signOut().catch(() => undefined);
      window.location.href = 'https://fluent.ge/?deleted=1';
      return true;
    } catch {
      return false;
    }
  };

  return (
    <SettingsView
      data={{
        displayName,
        email: user.email ?? '—',
        avatarUrl: profile.avatar_url ?? undefined,
        hasOauthPicture,
        avatarBusy,
        avatarNotice,
        goal: currentGoal,
        newCards: currentNewCards,
        locale: currentLocale,
        soundOn,
      }}
      onBack={() => navigate('/profile')}
      onPickAvatar={(f) => void onPickAvatar(f)}
      onSyncOAuth={() => void onSyncOAuth()}
      onRemoveAvatar={() => void onRemoveAvatar()}
      onChangeGoal={(g) => saveField('daily_goal_minutes', g)}
      onChangeNewCards={(n) => saveField('flashcards_new_per_day', n)}
      onChangeLocale={(l) => {
        void i18n.changeLanguage(l);
        saveField('locale', l, (settled) => void i18n.changeLanguage(settled ?? 'ka'));
      }}
      onToggleSound={(next) => {
        setSoundOn(next);
        setSoundMuted(!next);
        if (next) playSfx('correct'); // sample of what they just turned on
      }}
      onConfirmDelete={onConfirmDelete}
    />
  );
}

export interface SettingsViewData {
  displayName: string;
  email: string;
  avatarUrl?: string;
  hasOauthPicture: boolean;
  avatarBusy: AvatarBusy;
  /** Inline notice under the photo rows (validation / no-Google-photo). */
  avatarNotice: string | null;
  goal: Goal;
  newCards: NewCardLimit;
  locale: Locale;
  soundOn: boolean;
}

export interface SettingsViewProps {
  data: SettingsViewData;
  onBack: () => void;
  onPickAvatar: (file: File) => void;
  onSyncOAuth: () => void;
  onRemoveAvatar: () => void;
  onChangeGoal: (goal: Goal) => void;
  onChangeNewCards: (n: NewCardLimit) => void;
  onChangeLocale: (locale: Locale) => void;
  onToggleSound: (on: boolean) => void;
  /** Resolves false on failure → dialog shows the generic error. */
  onConfirmDelete: () => Promise<boolean>;
}

export function SettingsView({
  data,
  onBack,
  onPickAvatar,
  onSyncOAuth,
  onRemoveAvatar,
  onChangeGoal,
  onChangeNewCards,
  onChangeLocale,
  onToggleSound,
  onConfirmDelete,
}: SettingsViewProps) {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteConfirmWord = t('settings.delete.confirm_word');
  const deleteCanProceed =
    deleteConfirmText.trim().toLowerCase() === deleteConfirmWord.toLowerCase();

  const confirmDelete = async () => {
    setDeleteBusy(true);
    setDeleteError(null);
    const ok = await onConfirmDelete();
    if (!ok) {
      setDeleteError(t('settings.delete.error_generic'));
      setDeleteBusy(false);
    }
    // On success the page navigates away; leave busy on so the dialog
    // can't be reopened/re-fired during the redirect.
  };

  const anyAvatarBusy = data.avatarBusy !== null;

  return (
    <Screen>
      <Container size="sm">
        <div className="fluent-settings">
          <BackLink label={t('common.back')} onPress={onBack} />
          <Heading level={1}>{t('settings.title')}</Heading>

          {/* ---------- identity + photo ---------- */}
          <RowGroup>
            <div className="fluent-settings__identity">
              <span className="fluent-settings__identity-avatar">
                <Avatar size={64} name={data.displayName} src={data.avatarUrl} />
              </span>
              <div className="fluent-settings__identity-text">
                <Text weight="bold" className="fluent-settings__identity-name">
                  {data.displayName}
                </Text>
                <Text tone="muted" size="sm" className="fluent-settings__identity-email">
                  {data.email}
                </Text>
              </div>
            </div>
            {/* aria-disabled (not native disabled) while an operation runs,
                so keyboard focus survives and the rows stay in the Tab
                order; the onPress guard makes the disable real. */}
            <FileTrigger accept="image/jpeg,image/png,image/webp" onSelect={onPickAvatar}>
              {(open) => (
                <NavRow
                  variant="row"
                  chevron={false}
                  icon={<RowTile tone="primary">{<CameraGlyph />}</RowTile>}
                  title={t('settings.avatar.upload')}
                  sub={t('settings.avatar.hint')}
                  trailing={data.avatarBusy === 'upload' ? <Spinner size={18} /> : undefined}
                  onPress={() => {
                    if (!anyAvatarBusy) open();
                  }}
                  aria-disabled={anyAvatarBusy || undefined}
                />
              )}
            </FileTrigger>
            {data.hasOauthPicture && (
              <NavRow
                variant="row"
                chevron={false}
                icon={<RowTile tone="info">{<RefreshGlyph />}</RowTile>}
                title={t('settings.avatar.use_google')}
                trailing={data.avatarBusy === 'sync' ? <Spinner size={18} /> : undefined}
                onPress={() => {
                  if (!anyAvatarBusy) onSyncOAuth();
                }}
                aria-disabled={anyAvatarBusy || undefined}
              />
            )}
            {data.avatarUrl && (
              <NavRow
                variant="row"
                chevron={false}
                icon={<RowTile tone="danger">{<TrashGlyph />}</RowTile>}
                title={
                  <span className="fluent-settings__danger-title">
                    {t('settings.avatar.remove')}
                  </span>
                }
                trailing={data.avatarBusy === 'remove' ? <Spinner size={18} /> : undefined}
                onPress={() => {
                  if (!anyAvatarBusy) onRemoveAvatar();
                }}
                aria-disabled={anyAvatarBusy || undefined}
              />
            )}
            {/* Always-mounted live region — a region that mounts together
                with its content is typically NOT announced. */}
            <div className="fluent-settings__notice" role="status">
              {data.avatarNotice ? (
                <Text tone="danger" size="sm">
                  {data.avatarNotice}
                </Text>
              ) : null}
            </div>
          </RowGroup>

          {/* ---------- learning ---------- */}
          <SectionHead tone="primary" icon={<TargetGlyph />} label={t('settings.section.learning')} />
          <RowGroup>
            <SettingBlock
              tile={<RowTile tone="primary">{<TargetGlyph />}</RowTile>}
              title={t('settings.daily_goal.title')}
              sub={t('settings.daily_goal.subtitle')}
            >
              <SegmentedControl
                tone="primary"
                ariaLabel={t('settings.daily_goal.title')}
                options={GOALS.map((g) => ({
                  value: g,
                  label: g,
                  sub: t('settings.daily_goal.minutes'),
                }))}
                value={data.goal}
                onChange={onChangeGoal}
              />
            </SettingBlock>
            <SettingBlock
              tile={<RowTile tone="info">{<CardsGlyph />}</RowTile>}
              title={t('settings.flashcards.title')}
              sub={t('settings.flashcards.subtitle')}
            >
              <SegmentedControl
                tone="info"
                ariaLabel={t('settings.flashcards.title')}
                options={NEW_CARD_LIMITS.map((n) => ({
                  value: n,
                  label: n,
                  sub: t('settings.flashcards.cards'),
                }))}
                value={data.newCards}
                onChange={onChangeNewCards}
              />
            </SettingBlock>
          </RowGroup>

          {/* ---------- app ---------- */}
          <SectionHead tone="info" icon={<PhoneGlyph />} label={t('settings.section.app')} />
          <RowGroup>
            <SettingBlock
              tile={<RowTile tone="info">{<GlobeGlyph />}</RowTile>}
              title={t('settings.language.title')}
              sub={t('settings.language.subtitle')}
            >
              <SegmentedControl
                tone="info"
                ariaLabel={t('settings.language.title')}
                options={LOCALES.map((l) => ({ value: l.code, label: l.label }))}
                value={data.locale}
                onChange={onChangeLocale}
              />
            </SettingBlock>
            <SettingBlock
              tile={<RowTile tone="neutral">{<ContrastGlyph />}</RowTile>}
              title={t('settings.theme.title')}
              sub={t('settings.theme.subtitle')}
            >
              <SegmentedControl
                tone="neutral"
                ariaLabel={t('settings.theme.title')}
                options={[
                  { value: 'light', label: t('settings.theme.light') },
                  { value: 'system', label: t('settings.theme.auto_short') },
                  { value: 'dark', label: t('settings.theme.dark') },
                ]}
                value={theme}
                onChange={setTheme}
              />
            </SettingBlock>
            <div className="fluent-settings__inline-row">
              <RowTile tone="primary">{<SpeakerGlyph />}</RowTile>
              {/* Label lives inside the Toggle so the text is tappable. */}
              <Toggle
                className="fluent-settings__sound-toggle"
                label={t('profile.sound_label')}
                checked={data.soundOn}
                onChange={(e) => onToggleSound(e.target.checked)}
              />
            </div>
          </RowGroup>

          {/* ---------- account ---------- */}
          <SectionHead tone="neutral" icon={<ShieldGlyph />} label={t('settings.account.title')} />
          <RowGroup>
            <div className="fluent-settings__inline-row">
              <RowTile tone="neutral">{<MailGlyph />}</RowTile>
              {/* Stacked label + value — a trailing value can collide with
                  the label at 320px; a second line never can. */}
              <div className="fluent-settings__inline-text">
                <Text weight="bold">{t('profile.email')}</Text>
                <Text tone="muted" size="sm" weight="bold" className="fluent-settings__email-value">
                  {data.email}
                </Text>
              </div>
            </div>
            <NavRow
              variant="row"
              icon={<RowTile tone="danger">{<TrashGlyph />}</RowTile>}
              title={
                <span className="fluent-settings__danger-title">{t('settings.delete.cta')}</span>
              }
              sub={t('settings.delete.row_sub')}
              onPress={() => {
                setDeleteOpen(true);
                setDeleteConfirmText('');
                setDeleteError(null);
              }}
            />
          </RowGroup>
        </div>
      </Container>

      {/* Delete-account confirmation — type-to-confirm so the user can't
          fat-finger the danger button. */}
      <Dialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (deleteBusy) return; // can't dismiss while in-flight
          setDeleteOpen(open);
        }}
        title={t('settings.delete.dialog_title')}
        description={t('settings.delete.dialog_body')}
        dismissible={!deleteBusy}
        actions={
          <>
            <Button
              variant="neutral"
              size="md"
              onClick={() => setDeleteOpen(false)}
              disabled={deleteBusy}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              size="md"
              onClick={() => void confirmDelete()}
              loading={deleteBusy}
              disabled={!deleteCanProceed || deleteBusy}
            >
              {t('settings.delete.confirm_cta')}
            </Button>
          </>
        }
      >
        <div className="fluent-settings__delete-body">
          <Text tone="muted" size="sm">
            {t('settings.delete.confirm_prompt', { word: deleteConfirmWord })}
          </Text>
          <TextInput
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={deleteConfirmWord}
            autoFocus
            disabled={deleteBusy}
            aria-label={t('settings.delete.confirm_prompt', { word: deleteConfirmWord })}
          />
          {deleteError && (
            <Text tone="danger" size="sm">
              {deleteError}
            </Text>
          )}
        </div>
      </Dialog>
    </Screen>
  );
}

/* ---------- local presentational bits (no interactive raw HTML) ---------- */

type TileTone = 'primary' | 'info' | 'danger' | 'neutral';

function RowTile({ tone, children }: { tone: TileTone; children: ReactElement }) {
  return (
    <span className="fluent-settings__tile" data-tone={tone} aria-hidden="true">
      <Icon size={22}>{children}</Icon>
    </span>
  );
}

function SectionHead({ tone, icon, label }: { tone: TileTone; icon: ReactElement; label: string }) {
  return (
    <div className="fluent-settings__section-head">
      <span className="fluent-settings__section-coin" data-tone={tone} aria-hidden="true">
        <Icon size={14}>{icon}</Icon>
      </span>
      <Text size="sm" weight="bold" tone="muted">
        {label}
      </Text>
    </div>
  );
}

/** A setting with its control on a full-width line below the header. */
function SettingBlock({
  tile,
  title,
  sub,
  children,
}: {
  tile: ReactNode;
  title: string;
  sub?: string;
  children: ReactNode;
}) {
  return (
    <div className="fluent-settings__block">
      <div className="fluent-settings__block-head">
        {tile}
        <div className="fluent-settings__block-text">
          <Text weight="bold">{title}</Text>
          {sub && (
            <Text tone="muted" size="sm">
              {sub}
            </Text>
          )}
        </div>
      </div>
      <div className="fluent-settings__block-control">{children}</div>
    </div>
  );
}
